import React, { createContext, useState } from "react";
import { useDispatch } from "react-redux";
import { engineCloudURL } from "../urls";
import { io, Socket } from "socket.io-client";
import { isElectronApp } from "../electron-modules";
import {
  wsCreateFolder,
  wsDeleteFolder,
  wsUpdateFolder,
} from "../redux/folders-slice";
import {
  wsCreateAsset,
  wsDeleteAsset,
  wsUpdateAsset,
} from "../redux/assets-slice";
import {
  wsCreateBookmark,
  wsDeleteBookmark,
  wsUpdateBookmark,
} from "../redux/bookmarks-slice";

enum EventType {
  JoinRoom = "join_room",
  LeaveRoom = "leave_room",
  CreateObject = "create_asset",
  DeleteObject = "delete_asset",
  UpdateObject = "update_asset",
}

enum ObjectType {
  Assets = "assets",
  Folders = "folders",
  Bookmarks = "bookmarks"
}

export const WebSocketContext = createContext({
  connect: (accessToken: string) => {},
  disconnect: () => {},
  joinRoom: (roomID: string) => {}
});

export const WebSocketProvider = (props) => {
  const dispatch = useDispatch();

  const [socket, setSocket] = useState<Socket>(null);
  const [currentRoomID, setCurrentRoomID] = useState<string>(null);

  const connect = (accessToken: string) => {
    let extraHeaders = {};

    if (isElectronApp) {
      extraHeaders = {
        Authorization: `Bearer ${accessToken}`
      };
    }

    return new Promise(resolve => {
      const socket = io(engineCloudURL, {
        addTrailingSlash: false,
        extraHeaders
      });

      setSocket(socket);

      socket.on("connect", () => {
        register(socket);
        resolve(true);
      });

      socket.on("connect_error", () => {
        socket.disconnect();
        resolve(false);
      });
    });
  };

  const disconnect = () => {
    if (!socket?.connected) return;

    socket.disconnect();
    setSocket(null);
  };

  const onCreate = (message) => {
    const {type, data} = message;

    if (type === ObjectType.Assets) {
      dispatch(wsCreateAsset(data));
    } else if (type === ObjectType.Folders) {
      dispatch(wsCreateFolder(data));
    } else if (type === ObjectType.Bookmarks) {
      dispatch(wsCreateBookmark(data));
    }
  };

  const onUpdate = (message) => {
    const {type, data} = message;

    if (type === ObjectType.Assets) {
      dispatch(wsUpdateAsset(data));
    } else if (type === ObjectType.Folders) {
      dispatch(wsUpdateFolder(data));
    } else if (type === ObjectType.Bookmarks) {
      dispatch(wsUpdateBookmark(data));
    }
  };

  const onDelete = (message) => {
    const {type, objectID} = message;

    if (type === ObjectType.Assets) {
      dispatch(wsDeleteAsset(objectID));
    } else if (type === ObjectType.Folders) {
      dispatch(wsDeleteFolder(objectID));
    } else if (type === ObjectType.Bookmarks) {
      dispatch(wsDeleteBookmark(objectID));
    }
  };

  const register = (socket: Socket) => {
    socket.on(EventType.CreateObject, onCreate);
    socket.on(EventType.UpdateObject, onUpdate);
    socket.on(EventType.DeleteObject, onDelete);
  };

  const joinRoom = (roomID: string) => {
    if (!socket?.connected) return;

    if (currentRoomID) {
      // Leave previously joined room
      socket.emit(EventType.LeaveRoom, currentRoomID);
    }

    socket.emit(EventType.JoinRoom, roomID);
    setCurrentRoomID(roomID);
  };

  return (
    <WebSocketContext.Provider value={{
      connect,
      disconnect,
      joinRoom
    }}>
      {props.children}
    </WebSocketContext.Provider>
  );
};